import { useState, useEffect, createContext, useContext, useCallback } from "react";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';
const USER_KEY = 'fruitCrush_currentUser';

// API helper
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
};

// Get leaderboard
export const getLeaderboard = async () => {
  try {
    const data = await apiCall('/api/leaderboard');
    // Backend now sends camelCase, use it directly (with fallback for legacy data)
    return data.map(entry => ({
      playerName: entry.playerName || entry.player_name,
      score: entry.score,
      date: entry.date,
      level: entry.level || 1,
      prestige: entry.prestige || 0,
      isMaster: entry.is_master || false
    }));
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error);
    // Fallback to localStorage
    const leaderboard = localStorage.getItem('fruitCrush_leaderboard');
    return leaderboard ? JSON.parse(leaderboard) : [];
  }
};

// Update leaderboard with new score
export const updateLeaderboard = async (playerName, score) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      return await apiCall('/api/leaderboard', {
        method: 'POST',
        body: JSON.stringify({ score })
      });
    }
  } catch (error) {
    console.error('Failed to update leaderboard:', error);
  }
  
  // Fallback to localStorage
  const leaderboard = JSON.parse(localStorage.getItem('fruitCrush_leaderboard') || '[]');
  const existingIndex = leaderboard.findIndex(entry => entry.playerName === playerName);
  
  if (existingIndex >= 0) {
    if (score > leaderboard[existingIndex].score) {
      leaderboard[existingIndex].score = score;
      leaderboard[existingIndex].date = new Date().toISOString();
    }
  } else {
    leaderboard.push({ playerName, score, date: new Date().toISOString() });
  }
  
  leaderboard.sort((a, b) => b.score - a.score);
  const top100 = leaderboard.slice(0, 100);
  localStorage.setItem('fruitCrush_leaderboard', JSON.stringify(top100));
  return top100;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem(USER_KEY);
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [syncEnabled, setSyncEnabled] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        try {
          const userData = await apiCall('/api/auth/me');
          const userSession = {
            id: userData.id,
            email: userData.email,
            playerName: userData.player_name
          };
          setUser(userSession);
          localStorage.setItem(USER_KEY, JSON.stringify(userSession));
          setSyncEnabled(true);
        } catch (error) {
          console.log('Token invalid, using local session');
          // Keep local user if exists
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Register new user
  const register = async (email, password) => {
    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      localStorage.setItem(TOKEN_KEY, response.token);
      const userSession = {
        id: response.user.id,
        email: response.user.email,
        playerName: response.user.player_name
      };
      setUser(userSession);
      localStorage.setItem(USER_KEY, JSON.stringify(userSession));
      setSyncEnabled(true);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const response = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      // Check if profile selection is required
      if (response.requires_profile_selection) {
        return { 
          success: false, 
          requiresProfileSelection: true,
          profiles: response.profiles,
          email: response.email,
          password: password // Pass password for profile selection
        };
      }
      
      localStorage.setItem(TOKEN_KEY, response.token);
      const userSession = {
        id: response.user.id,
        email: response.user.email,
        playerName: response.user.player_name
      };
      setUser(userSession);
      localStorage.setItem(USER_KEY, JSON.stringify(userSession));
      setSyncEnabled(true);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Select a profile (when multiple profiles exist)
  const selectProfile = async (email, password, profileId) => {
    try {
      const response = await apiCall('/api/auth/select-profile', {
        method: 'POST',
        body: JSON.stringify({ email, password, profile_id: profileId })
      });
      
      localStorage.setItem(TOKEN_KEY, response.token);
      const userSession = {
        id: response.user.id,
        email: response.user.email,
        playerName: response.user.player_name
      };
      setUser(userSession);
      localStorage.setItem(USER_KEY, JSON.stringify(userSession));
      setSyncEnabled(true);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Create a new profile for an existing email
  const createProfile = async (email, password, playerName) => {
    try {
      const response = await apiCall('/api/auth/create-profile', {
        method: 'POST',
        body: JSON.stringify({ email, password, player_name: playerName })
      });
      
      localStorage.setItem(TOKEN_KEY, response.token);
      const userSession = {
        id: response.user.id,
        email: response.user.email,
        playerName: response.user.player_name
      };
      setUser(userSession);
      localStorage.setItem(USER_KEY, JSON.stringify(userSession));
      setSyncEnabled(true);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Set player name
  const setPlayerName = async (name) => {
    if (!user) return { success: false, error: "Not logged in" };
    
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        const response = await apiCall('/api/auth/set-name', {
          method: 'POST',
          body: JSON.stringify({ player_name: name })
        });
        
        const updatedUser = { ...user, playerName: response.player_name };
        setUser(updatedUser);
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
    
    // Fallback - shouldn't happen with API
    return { success: false, error: "Failed to set name" };
  };

  // Logout
  const logout = () => {
    setUser(null);
    setSyncEnabled(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  };

  // Get all profiles for current user's email (no password needed)
  const getProfiles = async () => {
    try {
      const data = await apiCall('/api/auth/profiles');
      return { success: true, profiles: data.profiles, email: data.email };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Switch to a different profile (no password needed)
  const switchProfile = async (profileId) => {
    try {
      const response = await apiCall('/api/auth/switch-profile', {
        method: 'POST',
        body: JSON.stringify({ profile_id: profileId })
      });

      localStorage.setItem(TOKEN_KEY, response.token);
      const userSession = {
        id: response.user.id,
        email: response.user.email,
        playerName: response.user.player_name
      };
      setUser(userSession);
      localStorage.setItem(USER_KEY, JSON.stringify(userSession));
      setSyncEnabled(true);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      register, 
      login,
      selectProfile,
      createProfile,
      logout, 
      setPlayerName,
      getProfiles,
      switchProfile,
      isAuthenticated: !!user,
      hasPlayerName: !!user?.playerName,
      syncEnabled
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
